import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as LegalIcon
} from '@mui/icons-material';
import styles from '../../styles/FAQs.module.css';

function FAQs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleFaqAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  // LAANC Regulatory FAQs
  const laancFaqs = [
    {
      question: "What is LAANC?",
      answer: "The Federal Aviation Administration (FAA) and private companies have partnered to create LAANC, or Low Altitude Authorization and Notification Capability, which allows drone pilots to enter controlled airspace up to 400 feet, grants air traffic controllers' visibility into drone operations, and provides guidance on flight restrictions."
    },
    {
      question: "How and when can drone pilots use LAANC?",
      answer: "Authorization Procedure for Drone Airspace:\n• Before flying in controlled airspace below 400 feet, drone operators must get airspace authorization from the FAA.\n• Users must register your drone and hold a Remote Pilot Certificate.\n• Recreational flyers must take The Recreational UAS Safety Test (TRUST) before they fly.\n• LAANC allows 90-day requests for airspace authorization."
    },
    {
      question: "Is LAANC available for all controlled airspace in the US?",
      answer: "LAANC is not available in all US controlled airspace, but primarily covers areas partnered with service providers. Since it's still expanding, always check coverage details before flying."
    },
    {
      question: "Can I combine other waivers or authorizations with LAANC?",
      answer: "No, LAANC is specifically designed to handle airspace authorizations for commercial drone operators and recreational flyers in controlled airspace. It's not intended to be combined with other types of waivers or authorizations."
    },
    {
      question: "How far in advance can I apply for LAANC?",
      answer: "Up to ninety days before the date of your intended flight, you can apply for LAANC. This advance authorization is useful for pre-planned activities since it enables you to obtain approval for a certain time, date, and location within regulated airspace."
    },
    {
      question: "What do I do with LAANC approval?",
      answer: "LAANC approval confirms your authorization to fly in controlled airspace.\n\n• Review Your Approval: Verify the details of your flight, such as the location and time, as well as the facts of your LAANC approval.\n• Conform with Conditions: Follow any restrictions or requirements mentioned in the approval.\n• Document Your Approval: In case you need to show it during your flight, have a copy of the approval on hand.\n• Get Ready to Fly: Before taking off, make sure your drone is ready and complies with all safety requirements."
    },
    {
      question: "Who regulates my airspace?",
      answer: "The Federal Aviation Administration (FAA) regulates airspace in the United States. They oversee all aspects of national airspace, including commercial, recreational, and military flights, to ensure safe and efficient operations. For drone pilots, the FAA sets rules and grants authorizations (like LAANC) for operating in controlled airspace near airports."
    },
    {
      question: "What does it mean to be approved by the FAA?",
      answer: "Being approved by the FAA indicates that you have been given permission to carry out aviation-related tasks, such as operating a drone in regulated airspace, in compliance with FAA guidelines. You may operate responsibly without endangering other airspace users due to this approval, which guarantees that you comply with safety regulations and legal obligations."
    },
    {
      question: "What are the requirements that are needed for recreational flying?",
      answer: "Recreational drone operators are required to register drones that weigh more than 0.55 pounds, fly under 400 feet, stay in Class G airspace unless authorized by the LAANC, stay away from crowds, and keep visual line-of-sight. They also need to pass the safety test known as TRUST."
    },
    {
      question: "What are Recreational and Part 107 Drone Operations?",
      answer: "Recreational Flying: This is for hobbyist pilots flying for personal enjoyment. LAANC approval is required in controlled airspace.\n\nPart 107 Operations: This is for commercial or professional drone pilots who must hold a Part 107 Remote Pilot Certificate. Part 107 pilots need LAANC approval to operate in controlled airspace."
    },
    {
      question: "What is B4UFLY?",
      answer: "Drone operators can use the B4UFLY service to verify airspace conditions and limitations before flying. It is operated by the FAA and helps users follow rules and guarantee safe drone operations in their area by giving them up-to-date information on no-fly zones, temporary flight restrictions, and local airports."
    },
    {
      question: "What are UAS Facility Maps (UASFMs)?",
      answer: "UAS Facility Maps (UASFMs) are FAA-created maps indicating safe altitudes for drone operations in controlled airspace around airports. They simplify the airspace authorization process by showing altitude limits for routine drone operations."
    },
    {
      question: "Is LAANC data shared?",
      answer: "For safe airspace management, the FAA, drone operators, and authorized LAANC service providers exchange LAANC data, such as flight information and authorization approvals. Individual flight details and personal information are kept private and provided only when necessary for operational and safety reasons."
    },
    {
      question: "Can I fly my drone at night in controlled and uncontrolled airspace?",
      answer: "Yes, you can fly your drone at night in both controlled and uncontrolled airspace, but specific requirements apply:\n\n• Commercial Pilots: Must complete the updated FAA part 107 test or training, which includes night flying rules, and have anti-collision lighting on the drone visible for at least 3 statute miles.\n• Recreational Flyers: Must follow the FAA's guidelines for night flights, including using anti-collision lighting visible for 3 statute miles.\n• Controlled Airspace: Requires FAA authorization (via LAANC or Drone Zone) even at night for Part 107 operators."
    },
    {
      question: "What is a TFR?",
      answer: "Temporary Flight Restriction (TFR) is a temporary FAA-issued restriction on airspace use, often for safety or security reasons, such as during natural disasters, VIP visits, or major events. TFRs prohibit unauthorized aircraft, including drones."
    },
    {
      question: "What is SUA?",
      answer: "A Special Use Airspace (SUA) is a designated airspace where specific activities, like military training or testing, occur. SUAs restrict or limit civilian aircraft access to ensure safety and security during these operations, requiring authorization for entry."
    }
  ];

  // Filter FAQs based on search
  const filteredFaqs = searchQuery
    ? laancFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : laancFaqs;

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2 }}>
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Find answers to common questions about LAANC, FAA regulations, and drone operations.
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Looking for help with FlightLoop features?</strong> Check out our{' '}
            <a href="/help/guides" style={{ color: 'inherit', textDecoration: 'underline' }}>
              User Guides
            </a>{' '}
            for step-by-step instructions on using the platform.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 4 }}
        />

        <Divider sx={{ mb: 4 }} />

        {/* LAANC & Regulatory FAQs */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <LegalIcon color="primary" />
            <Typography variant="h5">LAANC & FAA Regulations</Typography>
            <Chip label={`${filteredFaqs.length} FAQs`} size="small" color="primary" />
          </Box>

          {filteredFaqs.length === 0 ? (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No FAQs found matching your search. Try different keywords.
              </Typography>
            </Paper>
          ) : (
            filteredFaqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `panel-${index}`}
                onChange={handleFaqAccordionChange(`panel-${index}`)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>

        {/* Still Have Questions */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, bgcolor: 'primary.light' }}>
          <Typography variant="h6" gutterBottom>
            Still Have Questions?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Can't find what you're looking for? Our support team is here to help.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <a href="/contactus" style={{ textDecoration: 'none' }}>
              <Chip
                label="Contact Support"
                color="primary"
                clickable
                sx={{ fontWeight: 500 }}
              />
            </a>
            <a href="/help/guides" style={{ textDecoration: 'none' }}>
              <Chip
                label="View User Guides"
                color="secondary"
                clickable
                sx={{ fontWeight: 500 }}
              />
            </a>
          </Box>
        </Paper>

        {/* External Resources */}
        <Box sx={{ mt: 4, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            External Resources
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Learn more from official FAA resources:
          </Typography>
          <Box component="ul" sx={{ mt: 2, pl: 3 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <a
                href="https://www.faa.gov/uas"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                FAA Unmanned Aircraft Systems (UAS)
              </a>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <a
                href="https://www.faa.gov/uas/programs_partnerships/data_exchange"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                FAA LAANC Program Information
              </a>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <a
                href="https://www.faa.gov/uas/recreational_fliers"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Recreational Flyers & Modeler Community-Based Organizations
              </a>
            </Typography>
            <Typography component="li" variant="body2">
              <a
                href="https://www.faa.gov/uas/commercial_operators"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Part 107 Commercial Operators
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default FAQs;
